import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import FontAwesomeIcon from "../../components/FontAwesomeIcon";
import BookingModal from '../../components/BookingModal';
import FAQSection from '../../components/FAQSection';

const ChimpanzeeTrekking = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const faqs = [
    {
      question: "What's the difference between chimpanzee trekking and gorilla trekking?",
      answer: "Chimps are more active, playful, and vocal than gorillas. They swing through trees, use tools, and communicate with loud pant-hooting. Trekking is generally less strenuous (2-5 hours vs 4-8 hours), less expensive ($240 vs $800), and has higher success rates (95% vs 90%). Chimps allow closer approach (8m vs 10m)."
    },
    {
      question: "What is the success rate for seeing chimpanzees?",
      answer: "Kibale has the highest chimp tracking success rate in Africa at 95%+. Trackers monitor habituated families daily and use radio communication to locate them efficiently. Success is nearly guaranteed year-round thanks to Kibale's dense chimp population (1,500+) and experienced tracking teams."
    },
    {
      question: "How close can we get to the chimpanzees?",
      answer: "UWA requires an 8-meter (26-foot) minimum distance to protect chimps from human diseases and ensure safety. However, habituated chimps often approach closer on their own accord - they may pass within meters as they forage or play. Remain calm and follow guide instructions if chimps come very close."
    },
    {
      question: "Can I trek if I'm not very fit?",
      answer: "Yes! Chimpanzee trekking is considerably less demanding than gorilla trekking. Treks range from 2-5 hours depending on chimp location, with moderate terrain and less extreme elevation gain. Porters ($15) can carry your daypack and provide physical assistance on difficult sections."
    },
    {
      question: "What time of year is best for chimpanzee trekking?",
      answer: "Kibale offers excellent chimp trekking year-round with consistent 95%+ success rates. Dry seasons (Dec-Feb, Jun-Sep) provide easier trekking conditions and clearer forest views. Wet seasons (Mar-May, Oct-Nov) bring lush, photogenic landscapes, fewer tourists, and lower accommodation rates."
    }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Mitchell",
      location: "London, UK",
      date: "March 2024",
      rating: 5,
      text: "The chimpanzee trek in Kibale was absolutely extraordinary! Watching these intelligent primates interact, groom each other, and communicate with pant-hooting calls was mesmerizing. Our guide's knowledge about their social dynamics and individual personalities made the experience even richer. The 95% success rate is no exaggeration - we found our group within 2 hours!"
    },
    {
      name: "Marcus Johnson",
      location: "Toronto, Canada",
      date: "February 2024",
      rating: 5,
      text: "As a wildlife photographer, Kibale exceeded all expectations. The lighting in the forest is challenging but magical, and being within meters of these expressive primates created once-in-a-lifetime shots. The trek was moderately challenging but much easier than gorilla trekking - perfect for my parents who joined us. Highly recommend!"
    },
    {
      name: "Emma van der Berg",
      location: "Amsterdam, Netherlands",
      date: "January 2024",
      rating: 5,
      text: "What struck me most was how playful and human-like the chimps are! We watched juveniles swinging through the canopy, mothers caring for infants, and the alpha male asserting dominance. The 3-hour trek through Kibale's stunning rainforest was an adventure in itself. Conservation fees well spent - knowing we're supporting primate protection matters."
    },
    {
      name: "Rajesh Kumar",
      location: "Mumbai, India",
      date: "December 2023",
      rating: 5,
      text: "Kibale truly is the Primate Capital of East Africa! Beyond chimps, we spotted red colobus monkeys, black-and-white colobus, and L'Hoest's monkeys during our trek. The biodiversity here is staggering - 375 bird species and 13 primate species in one forest! Our guide was exceptional, explaining chimp behavior and forest ecology beautifully."
    }
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Chimpanzee Trekking in Kibale Forest
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Encounter our closest relatives in Africa's Primate Capital—an intimate journey into the world of intelligent chimpanzees
            </p>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                Protected by UWA
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-award" className="w-4 h-4 mr-2" />
                95% Success Rate
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Chimp Trek
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Experience the Primate Capital of East Africa
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Kibale National Park is widely regarded as the <strong>Primate Capital of East Africa</strong>, harboring an extraordinary diversity of 13 primate species within its lush, tropical rainforest. Among them, the park's population of over 1,500 chimpanzees represents one of the most habituated and accessible communities on the African continent, offering visitors an unparalleled opportunity to observe our closest living relatives in their natural environment.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  A chimpanzee trek in Kibale is a journey into a world of intelligence, emotion, and complex social structures that mirror our own humanity. As you venture into the verdant forest at dawn, guided by expert Uganda Wildlife Authority trackers, the air fills with the distinctive pant-hooting calls of chimps communicating across the canopy. The trek itself typically lasts 2-5 hours, winding through ancient mahogany trees, strangler figs, and dense undergrowth teeming with life - brilliant butterflies, exotic birds, and troops of red colobus and L'Hoest's monkeys.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  When you finally encounter a chimpanzee family, the experience is nothing short of transformative. Watch as juveniles somersault through vines with abandon, mothers tenderly groom their infants, and the alpha male surveys his domain with quiet authority. You'll witness tool use - chimps fashioning sticks to extract termites, cracking nuts with stones - behaviors that demonstrate their remarkable cognitive abilities. Their expressive faces convey emotions we instantly recognize: joy, frustration, curiosity, affection.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The trek's <strong>95% success rate</strong> is among the highest in Africa, thanks to dedicated researchers and trackers who monitor chimp movements daily. Kibale's habituated groups have grown comfortable with human presence, allowing for relaxed, extended observations that reveal the intricacies of chimp society. You'll spend a precious hour in their company, though time seems suspended as you're absorbed in their world.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Your $240 permit fee directly funds Uganda Wildlife Authority's conservation programs, anti-poaching patrols, habitat restoration, and community outreach initiatives that make human-wildlife coexistence possible. By choosing this trek, you become a stakeholder in preserving Kibale's precious ecosystem and ensuring that future generations can experience the wonder of wild chimpanzees.
                </p>
              </div>
            </section>

            {/* Activity Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Trek Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: "fa-leaf", title: "Africa's Primate Capital", desc: "13 primate species including 1,500+ chimpanzees" },
                  { icon: "fa-check-circle", title: "95% Success Rate", desc: "Highest chimp encounter rate in East Africa" },
                  { icon: "fa-heart", title: "Expressive Interactions", desc: "Watch grooming, tool use, and social bonding" },
                  { icon: "fa-binoculars", title: "Close Encounters", desc: "Habituated groups allow relaxed observations" },
                  { icon: "fa-camera", title: "Photography Paradise", desc: "Capture incredible primate behavior moments" },
                  { icon: "fa-tree", title: "Rainforest Biodiversity", desc: "375 bird species, forest elephants, rare flora" },
                  { icon: "fa-mountain", title: "Moderate Terrain", desc: "Less strenuous than gorilla trekking" },
                  { icon: "fa-globe", title: "Conservation Impact", desc: "Permits fund UWA protection & research programs" }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Sample Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your Trek Itinerary
              </h2>
              <div className="bg-safari-light border-l-4 border-safari-gold p-6 mb-8 rounded-r-lg">
                <p className="text-gray-700 text-lg">
                  <strong>⚠️ Important Note:</strong> This itinerary is tentative and can be customized based on your preferences,
                  pickup location, and which chimp community you're tracking. We can adjust timing and combine with other activities.
                </p>
              </div>
              <div className="space-y-6">
                {[
                  { time: "7:00 AM", title: "Kanyanchu Visitor Center", desc: "Arrive for registration and pre-trek briefing. Rangers explain chimp behavior, trekking etiquette, and safety protocols. Small groups of maximum 6 people assigned to habituated families." },
                  { time: "7:30 AM", title: "Trek Begins", desc: "Enter Kibale's rainforest with armed UWA guides and expert trackers. The trail winds through towering mahogany and fig trees, with frequent stops to identify primates, birds, and forest ecology." },
                  { time: "8:00-10:00 AM", title: "Tracking & Search", desc: "Trackers use radio communication and experience to locate chimps based on overnight nests and movement patterns. You may encounter red colobus, black-and-white colobus, and grey-cheeked mangabeys en route." },
                  { time: "Variable", title: "Chimp Encounter", desc: "First contact! Approach quietly as chimps feed, groom, or rest. The hour begins when you're within viewing distance. Observe tool use, juveniles playing, mothers nursing, and complex social interactions." },
                  { time: "1 Hour", title: "Observation Time", desc: "Spend 60 precious minutes watching, photographing, and absorbing chimp life. Your guide explains individual personalities, family structures, and behaviors. Pant-hooting calls echo through the canopy." },
                  { time: "After Observation", title: "Forest Return", desc: "Trek back to Kanyanchu through the forest, often spotting additional wildlife and birds. Share stories and photos with fellow trekkers." },
                  { time: "11:30 AM - 1:00 PM", title: "Trek Conclusion", desc: "Arrive back at the visitor center. Receive your trekking certificate. Optional: visit nearby Bigodi Wetland Sanctuary or participate in community tourism activities." }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time.split(' ')[0]}<br />{step.time.split(' ')[1]}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Frequently Asked Questions" />

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Essential Packing List
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: "fa-shoe-prints", item: "Waterproof hiking boots", note: "Ankle support essential" },
                  { icon: "fa-shirt", item: "Long-sleeved shirt & trousers", note: "Neutral colors, protection from thorns" },
                  { icon: "fa-droplet", item: "Rain jacket", note: "Weather is unpredictable" },
                  { icon: "fa-hand", item: "Gardening gloves", note: "For grabbing vegetation" },
                  { icon: "fa-spray-can", item: "Insect repellent", note: "DEET 30%+ recommended" },
                  { icon: "fa-camera", item: "Camera", note: "Good zoom lens, no flash" }
                ].map((item, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center flex-shrink-0">
                        <FontAwesomeIcon icon={item.icon} className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-safari-green mb-1">{item.item}</h3>
                        <p className="text-gray-600 text-sm">{item.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Testimonials */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What Our Guests Say
              </h2>
              <div className="card p-8 relative">
                <p className="text-lg text-gray-700 italic mb-6">
                  "{testimonials[currentTestimonial].text}"
                </p>
                <div className="flex justify-between items-center border-t border-gray-100 pt-6">
                  <div>
                    <div className="font-bold text-safari-green text-lg">
                      {testimonials[currentTestimonial].name}
                    </div>
                    <div className="text-gray-500 text-sm">
                      {testimonials[currentTestimonial].location} • {testimonials[currentTestimonial].date}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={prevTestimonial}
                      className="w-10 h-10 rounded-full border border-safari-green text-safari-green hover:bg-safari-green hover:text-white transition-colors flex items-center justify-center"
                    >
                      <FontAwesomeIcon icon="fa-chevron-left" className="w-4 h-4" />
                    </button>
                    <button
                      onClick={nextTestimonial}
                      className="w-10 h-10 rounded-full border border-safari-green text-safari-green hover:bg-safari-green hover:text-white transition-colors flex items-center justify-center"
                    >
                      <FontAwesomeIcon icon="fa-chevron-right" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Adventures You'll Love
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/gorilla-trekking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1551731409-43eb3e517a1a?w=800"
                      alt="Gorilla Trekking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Gorilla Trekking</h3>
                    <p className="text-gray-700 mb-4">The ultimate primate encounter in Bwindi Impenetrable Forest.</p>
                    <p className="text-safari-gold font-bold">From $800 per person</p>
                  </div>
                </Link>

                <Link to="/activities/chimp-habituation" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Chimp Habituation"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Chimp Habituation</h3>
                    <p className="text-gray-700 mb-4">Spend a full day with researchers and semi-wild chimpanzees.</p>
                    <p className="text-safari-gold font-bold">Permit fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 2-5 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Year-round</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Price:</strong> $240 per person</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-chart-simple" className="w-6 h-6 text-safari-green" />
                    <span><strong>Difficulty:</strong> Moderate</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Max 6 people</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Trek
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Chimpanzee trekking permit ($200)",
                    "Kibale National Park entrance fee ($40)",
                    "Expert UWA guides and trackers",
                    "Armed ranger escort for safety",
                    "1 hour with habituated chimp family",
                    "Trekking certificate",
                    "Park briefing and orientation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Transportation to Kibale",
                    "Accommodation",
                    "Meals and beverages",
                    "Porter hire ($15 optional)",
                    "Ranger tips",
                    "Personal trekking gear"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Fitness
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Fitness:</strong> Moderate. 2-5 hours walking on uneven terrain.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No infectious diseases (colds/flu) allowed.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Chimpanzee Trekking - Kibale Forest"
      />
    </div>
  );
};

export default ChimpanzeeTrekking;
